import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";

import useDidMountEffect from "../../utils/useDidMountEffect";
import NewUserModal from "./NewUserModal";

const UsersTable = (props) => {
  const { users, setUsers, selectedUser, setSelectedUser, userLimit } = props;
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [newUserModal, setNewUserModal] = useState("");

  const removeUser = (userId) => {
    fetch(`/users_management/${userId}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
    });

    setUsers(users.filter((user) => user.id !== userId));

    if (selectedUser && selectedUser.id === userId) {
      setSelectedUser(null);
    }
  };

  const fetchUsers = (search) => {
    let url = "/users_management";
    if (search) {
      url += "?search=" + search;
    }

    fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setUsers(data.users);
      });
  };

  useDidMountEffect(() => {
    fetchUsers(debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search, 500]);

  useEffect(() => {
    setSelectedUser(null);
  }, [users]);

  const usersTable = users.map((user, index) => (
    <tr
      key={index}
      className={`shadow ${
        selectedUser && selectedUser.id == user.id
          ? "bg-primaryYellow500"
          : "hover:bg-gray-50"
      }`}
      onClick={() => {
        setSelectedUser(user);
      }}
    >
      <td className="p-3">
        <div className="font-medium">{user.name}</div>
        <div className="text-xs text-gray-500">{user.email}</div>
      </td>
      <td className="p-3 text-right">
        <button
          onClick={() => {
            removeUser(user.id);
          }}
          className="cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
            />
          </svg>
        </button>
      </td>
    </tr>
  ));

  const addUserButtonClass = () => {
    let classes =
      "flex items-center gap-2 bg-black text-white px-4 py-2 text-sm font-medium";
    if (userLimitReached()) {
      classes += " opacity-30";
    }
    return classes;
  };

  const userLimitReached = () => {
    return userLimit && users.length >= userLimit;
  };

  return (
    <>
      {userLimitReached() && <Tooltip id="add-user-tooltip" />}
      {newUserModal && (
        <NewUserModal
          setNewUserModal={setNewUserModal}
          setUsers={setUsers}
          users={users}
        />
      )}
      <div className="w-96 flex flex-col gap-2">
        <button
          className={addUserButtonClass()}
          disabled={userLimitReached()}
          onClick={() => setNewUserModal(true)}
          data-tooltip-id="add-user-tooltip"
          data-tooltip-html="Osiągnięto limit użytkowników"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M12 4v16m8-8H4" />
          </svg>
          Dodaj Użytkownika
        </button>
        <div className="relative">
          <input
            type="text"
            placeholder="Wyszukaj..."
            className="pl-10 pr-8 py-1.5 border border-gray-300 text-sm w-full"
            onChange={(e) => setSearch(e.target.value)}
          />
          <svg
            className="w-4 h-4 absolute left-2.5 top-2 text-gray-400"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
          >
            <path d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1010.5 18a7.5 7.5 0 006.15-3.35z" />
          </svg>
        </div>
        <div className="h-full overflow-auto max-h-[70vh]">
          <table className="w-full text-left border-separate border-spacing-y-4">
            <tbody>{usersTable}</tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default UsersTable;

UsersTable.propTypes = {
  selectedUser: PropTypes.object,
  users: PropTypes.array,
  setUsers: PropTypes.func,
  setSelectedUser: PropTypes.func,
  userLimit: PropTypes.number,
};
